import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { baseUrl } from "../../Network";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { handleAuthError } from "../../utils";
import { isMarketerReadOnly } from "../../helpers/adminRole";
import { buildAiTutorCurriculumEditUrl } from "../../utils/aiTutorCurriculumUrl";
import withAuth from "../withAuth";

const AI_CURRICULUM_PREVIEW_URL =
  process.env.REACT_APP_AI_CURRICULUM_PREVIEW_URL ||
  "https://ai.rydlearning.com/curriculum-preview";

const buildPreviewUrl = (curriculumId) => {
  const code = btoa(String(curriculumId));
  return `${AI_CURRICULUM_PREVIEW_URL}?curriculumCode=${encodeURIComponent(code)}`;
};

const getAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null");
  } catch {
    return null;
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

/** Content edits only. Older rows without contentUpdatedAt fall back to updatedAt. */
const lastModifiedOf = (item) => item.contentUpdatedAt || item.updatedAt || null;

const ManageAiCurriculum = () => {
  document.title = "Manage Curriculum | RYD Admin";
  const readOnly = isMarketerReadOnly();
  const [loading, setLoading] = useState(true);
  const [curriculums, setCurriculums] = useState([]);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [curriculumToDelete, setCurriculumToDelete] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [curriculumToPreview, setCurriculumToPreview] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [curriculumToEdit, setCurriculumToEdit] = useState(null);
  const [sortByModified, setSortByModified] = useState(false);

  const sortedCurriculums = useMemo(() => {
    if (!sortByModified) return curriculums;
    const time = (item) => new Date(lastModifiedOf(item) || 0).getTime() || 0;
    return [...curriculums].sort((a, b) => time(b) - time(a));
  }, [curriculums, sortByModified]);

  const fetchCurriculums = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/admin/curriculum/all`);
      setCurriculums(response?.data?.data || []);
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to load curriculums");
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async (item) => {
    try {
      setTogglingId(item.id);
      const nextValue = !item.isVisible;
      const response = await axios.put(`${baseUrl}/admin/curriculum/visibility/${item.id}`, {
        isVisible: nextValue,
      });
      const updated = response?.data?.data;
      setCurriculums((prev) =>
        prev.map((c) =>
          c.id === item.id
            ? {
                ...c,
                isVisible: nextValue,
                publishedAt: updated?.publishedAt ?? c.publishedAt,
              }
            : c
        )
      );
      toast.success(nextValue ? "Curriculum is now visible to learners" : "Curriculum hidden from learners");
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to update visibility");
    } finally {
      setTogglingId(null);
    }
  };

  const openDeleteModal = (item) => {
    setCurriculumToDelete(item);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deletingId) return;
    setDeleteModal(false);
    setCurriculumToDelete(null);
  };

  const openPreviewModal = (item) => {
    setCurriculumToPreview(item);
    setPreviewModal(true);
  };

  const closePreviewModal = () => {
    setPreviewModal(false);
    setCurriculumToPreview(null);
  };

  const openEditModal = (item) => {
    setCurriculumToEdit(item);
    setEditModal(true);
  };

  const closeEditModal = () => {
    setEditModal(false);
    setCurriculumToEdit(null);
  };

  const handleConfirmPreview = () => {
    if (!curriculumToPreview) return;
    const url = buildPreviewUrl(curriculumToPreview.id);
    window.open(url, "_blank", "noopener,noreferrer");
    closePreviewModal();
  };

  const handleConfirmEdit = () => {
    if (!curriculumToEdit) return;
    try {
      const authUser = getAuthUser();
      const url = buildAiTutorCurriculumEditUrl(authUser, {
        slug: curriculumToEdit.slug,
        curriculumId: curriculumToEdit.id,
      });
      window.open(url, "_blank", "noopener,noreferrer");
      closeEditModal();
    } catch (error) {
      toast.error(error?.message || "Unable to open curriculum editor");
    }
  };

  const handleDeleteCurriculum = async () => {
    if (!curriculumToDelete) return;
    try {
      setDeletingId(curriculumToDelete.id);
      await axios.delete(`${baseUrl}/admin/curriculum/${curriculumToDelete.id}`);
      setCurriculums((prev) => prev.filter((c) => c.id !== curriculumToDelete.id));
      toast.success("Curriculum deleted successfully");
      setDeleteModal(false);
      setCurriculumToDelete(null);
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to delete curriculum");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    fetchCurriculums();
  }, []);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="AI Tools" breadcrumbItem="Manage Curriculum" />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">Loading...</div>
                ) : (
                  <>
                  <div className="d-flex justify-content-end mb-3">
                    <Button
                      type="button"
                      color="secondary"
                      outline={!sortByModified}
                      size="sm"
                      onClick={() => setSortByModified((v) => !v)}
                    >
                      {sortByModified ? "Sorted: recently modified" : "Sort by recently modified"}
                    </Button>
                  </div>
                  <div className="table-responsive">
                    <Table className="align-middle mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Slug</th>
                          <th>Title</th>
                          <th>Level</th>
                          <th>Language</th>
                          <th>Uploaded By</th>
                          <th>Uploader Email</th>
                          <th>Created</th>
                          <th>Last modified</th>
                          <th>Published</th>
                          <th>Visibility</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedCurriculums.map((item, index) => (
                          <tr key={item.id}>
                            <td>{index + 1}</td>
                            <td>{item.slug}</td>
                            <td>{item.title}</td>
                            <td>{item.level || "-"}</td>
                            <td>{item.language || "-"}</td>
                            <td>{item.uploadedByTeacherName || "-"}</td>
                            <td>{item.uploadedByTeacherEmail || "-"}</td>
                            <td>{formatDate(item.createdAt)}</td>
                            <td title={lastModifiedOf(item) ? new Date(lastModifiedOf(item)).toString() : undefined}>
                              {formatDate(lastModifiedOf(item))}
                            </td>
                            <td>{formatDate(item.publishedAt)}</td>
                            <td>
                              {readOnly ? (
                                <span className="text-muted">{item.isVisible ? "Visible" : "Hidden"}</span>
                              ) : (
                              <Button
                                type="button"
                                color={item.isVisible ? "warning" : "success"}
                                outline={!!item.isVisible}
                                size="sm"
                                className="d-inline-flex align-items-center gap-2 min-w-[5.5rem] justify-content-center"
                                disabled={togglingId === item.id}
                                onClick={() => toggleVisibility(item)}
                              >
                                {togglingId === item.id ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                    <span>Processing…</span>
                                  </>
                                ) : item.isVisible ? (
                                  "Hide"
                                ) : (
                                  "Show"
                                )}
                              </Button>
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  color="primary"
                                  outline
                                  size="sm"
                                  onClick={() => openPreviewModal(item)}
                                >
                                  Preview
                                </Button>
                                {!readOnly && (
                                  <>
                                    <Button
                                      type="button"
                                      color="info"
                                      outline
                                      size="sm"
                                      onClick={() => openEditModal(item)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      type="button"
                                      color="danger"
                                      outline
                                      size="sm"
                                      disabled={deletingId === item.id}
                                      onClick={() => openDeleteModal(item)}
                                    >
                                      {deletingId === item.id ? (
                                        <>
                                          <span
                                            className="spinner-border spinner-border-sm me-1"
                                            role="status"
                                            aria-hidden="true"
                                          />
                                          Deleting…
                                        </>
                                      ) : (
                                        "Delete"
                                      )}
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {!curriculums.length && (
                          <tr>
                            <td colSpan={12} className="text-center py-4">
                              No curriculum uploaded yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={previewModal} toggle={closePreviewModal}>
        <ModalHeader toggle={closePreviewModal}>Preview curriculum</ModalHeader>
        <ModalBody>
          <p>
            Open a preview of <strong>{curriculumToPreview?.title}</strong> in the AI Tutor app?
          </p>
          <p className="text-muted mb-0">This opens in a new browser tab.</p>
          <div className="d-flex justify-content-end mt-3">
            <Button color="secondary" onClick={closePreviewModal} className="me-2">
              Cancel
            </Button>
            <Button color="primary" onClick={handleConfirmPreview}>
              Open preview
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={editModal} toggle={closeEditModal}>
        <ModalHeader toggle={closeEditModal}>Edit curriculum</ModalHeader>
        <ModalBody>
          <p>
            Open <strong>{curriculumToEdit?.title}</strong> in the AI Tutor workspace to edit?
          </p>
          <p className="text-muted mb-0">
            You will be signed in with your admin session. Save there to update this curriculum.
          </p>
          <div className="d-flex justify-content-end mt-3">
            <Button color="secondary" onClick={closeEditModal} className="me-2">
              Cancel
            </Button>
            <Button color="info" onClick={handleConfirmEdit}>
              Open editor
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={deleteModal} toggle={closeDeleteModal}>
        <ModalHeader toggle={closeDeleteModal}>Confirm delete</ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to delete the curriculum{" "}
            <strong>{curriculumToDelete?.title}</strong>
            {curriculumToDelete?.slug ? (
              <>
                {" "}
                (<code>{curriculumToDelete.slug}</code>)
              </>
            ) : null}
            ?
          </p>
          <p className="text-muted mb-0">This action cannot be undone.</p>
          <div className="d-flex justify-content-end mt-3">
            <Button color="secondary" onClick={closeDeleteModal} className="me-2" disabled={!!deletingId}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleDeleteCurriculum} disabled={!!deletingId}>
              {deletingId ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Deleting…
                </>
              ) : (
                "Delete curriculum"
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default withAuth(ManageAiCurriculum);
